<script setup lang="ts">
import type { PropertyDetailed, ProjectLayout, AvailableLayoutGroup } from '~/types/properties';

const props = defineProps<{
  item: PropertyDetailed;
}>();

const { t } = useI18n();
const { formatNumber } = useFormat();

// Real floor-plan / site-plan images (image_meta floorplan/site_plan tags).
const planImages = computed<string[]>(() => (props.item as { layoutImages?: string[] }).layoutImages ?? []);
// Floor-plan PDFs (from the documents column).
const planDocs = computed<Array<{ title: string; src: string }>>(
  () => (props.item as { floor_plans?: Array<{ title: string; src: string }> }).floor_plans ?? [],
);
// Unit-type metadata (unit_layouts) — name/beds/area/price (no images: those paths are unreliable).
const unitTypes = computed<ProjectLayout[]>(() => (props.item as { layouts?: ProjectLayout[] }).layouts ?? []);
// "Available layouts" from projects.unit_mix — reliable type / from-price / size,
// grouped by category. Preferred over unitTypes, which rarely carries a price.
const availableLayouts = computed<AvailableLayoutGroup[]>(
  () => (props.item as { availableLayouts?: AvailableLayoutGroup[] }).availableLayouts ?? [],
);

const isEmpty = computed(
  () => !planImages.value.length && !planDocs.value.length && !unitTypes.value.length && !availableLayouts.value.length,
);

const numberFormatter = { format: (v: number) => formatNumber(v) };
const bedroomsLabel = (b: number | null | undefined): string | null =>
  b == null ? null : b <= 0 ? t('common.priceTable.studio') : t('property.v2Layouts.bedroomsCount', { count: b }, b);
const formatArea = (sqft: number | null | undefined): string | null =>
  sqft && sqft > 0 ? `${numberFormatter.format(Math.round(sqft))} sqft` : null;
const formatPrice = (aed: number | null | undefined): string | null =>
  aed && aed > 0 ? t('property.sharePopover.fromAed', { amount: numberFormatter.format(Math.round(aed)) }) : null;
const formatSizeRange = (from: number | null | undefined, to: number | null | undefined): string | null => {
  const f = from && from > 0 ? Math.round(from) : null;
  const dest = to && to > 0 ? Math.round(to) : null;
  if (f && dest && dest !== f) return `${numberFormatter.format(f)}–${numberFormatter.format(dest)} sqft`;
  if (f || dest) return `${numberFormatter.format((f ?? dest) as number)} sqft`;
  return null;
};
const hasMeta = (l: ProjectLayout): boolean =>
  bedroomsLabel(l.bedrooms) != null || formatArea(l.areaSqft) != null || formatPrice(l.priceFromAed) != null;

// Images live in unit_layouts (item.layouts); unit_mix cards match by type name.
const normType = (s: string | null | undefined): string => (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
const imagesByType = computed<Map<string, string[]>>(() => {
  const m = new Map<string, string[]>();
  for (const l of unitTypes.value) {
    const imgs = l.images ?? [];
    if (!imgs.length) continue;
    for (const key of [normType(l.type), normType(l.name)]) {
      if (key && !m.has(key)) m.set(key, imgs);
    }
  }
  return m;
});
const imagesFor = (typeOrName: string | null | undefined): string[] =>
  imagesByType.value.get(normType(typeOrName)) ?? [];

const lightboxImages = ref<string[]>([]);
const lightboxIndex = ref(0);
const openLightbox = (images: string[], startIndex = 0) => {
  if (!images.length) return;
  if (isLocked.value) {
    gate('generic');
    return;
  }
  lightboxImages.value = images;
  lightboxIndex.value = startIndex;
};

// Floor plans are gated: guests (no auth) see a blurred preview + unlock CTA;
// authenticated users see the real plans. Mirrors the document gate used on
// the Documents tab.
const { gate, level } = useAccessGate();
const isLocked = computed(() => level.value === 'guest');

const onDocClick = (doc: { title: string; src: string }, event: MouseEvent) => {
  const passed = gate(
    'document',
    () => {
      if (typeof window !== 'undefined') window.open(doc.src, '_blank', 'noopener,noreferrer');
    },
    {
      documentName: doc.title || t('property.v2Layouts.floorPlanAlt'),
      documentMeta: t('property.v2Layouts.floorPlanAlt'),
    },
  );
  if (!passed) event.preventDefault();
};
</script>

<template>
  <div class="pp-tab-surface">
    <div
      v-if="isEmpty"
      class="empty"
    >
      <p>{{ t('property.v2Layouts.empty') }}</p>
    </div>

    <template v-else>
      <!-- Floor / site plan images -->
      <section
        v-if="planImages.length"
        class="block"
      >
        <div class="pp-desc">
          <div class="eyebrow">{{ t('property.documentsTab.floorPlans') }}</div>
          <h3>{{ t('property.v2Layouts.floorSitePlansTitle') }}</h3>
        </div>
        <div
          class="plan-wrap"
          :class="{ locked: isLocked }"
        >
          <div class="plan-grid">
            <button
              v-for="(img, i) in planImages"
              :key="i"
              type="button"
              class="plan"
              :aria-label="t('property.v2Availability.viewFloorPlanAriaLabel')"
              @click="openLightbox(planImages, i)"
            >
              <img
                :src="img"
                :alt="t('property.v2Layouts.floorPlanAlt')"
                loading="lazy"
              />
            </button>
          </div>
          <div
            v-if="isLocked"
            class="lock-ov"
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.7"
              aria-hidden="true"
            >
              <rect
                x="3"
                y="11"
                width="18"
                height="11"
                rx="2"
              />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <p>{{ t('property.v2Layouts.lockMessage') }}</p>
            <button
              type="button"
              class="lock-cta"
              @click="gate('generic')"
            >
              {{ t('property.v2Layouts.unlockButton') }}
            </button>
          </div>
        </div>
      </section>

      <!-- Floor plan documents -->
      <section
        v-if="planDocs.length"
        class="block"
      >
        <div class="pp-desc">
          <div class="eyebrow">{{ t('property.shell.tabs.documents') }}</div>
          <h3>{{ t('property.v2Layouts.floorPlanDocumentsTitle') }}</h3>
        </div>
        <div class="docs">
          <a
            v-for="(d, i) in planDocs"
            :key="i"
            :href="d.src"
            target="_blank"
            rel="noopener noreferrer"
            class="doc"
            @click="onDocClick(d, $event)"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              aria-hidden="true"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            <span>{{ d.title || t('property.v2Layouts.floorPlanAlt') }}</span>
          </a>
        </div>
      </section>

      <!-- Available layouts — from projects.unit_mix: type · size · from-price,
           grouped by category. Preferred over the bare unit_layouts names. -->
      <section
        v-if="availableLayouts.length"
        class="block"
      >
        <div class="pp-desc">
          <div class="eyebrow">{{ t('property.v2Layouts.unitTypesEyebrow') }}</div>
          <h3>{{ t('property.v2Layouts.availableLayoutsTitle') }}</h3>
          <p>
            {{ t('property.v2Layouts.availableLayoutsDescription') }}
          </p>
        </div>
        <div
          v-for="grp in availableLayouts"
          :key="grp.category"
          class="cat-group"
        >
          <div
            v-if="availableLayouts.length > 1"
            class="cat-label"
          >
            {{ grp.category }}
          </div>
          <div class="types">
            <div
              v-for="(l, i) in grp.layouts"
              :key="`${l.type}-${i}`"
              class="type"
              :class="{ 'has-thumb': imagesFor(l.type).length }"
            >
              <button
                v-if="imagesFor(l.type).length"
                type="button"
                class="type-thumb"
                :aria-label="t('property.v2Layouts.viewLayoutAriaLabel')"
                @click="openLightbox(imagesFor(l.type), 0)"
              >
                <img
                  :src="imagesFor(l.type)[0]"
                  alt=""
                  loading="lazy"
                  @error="($event.target as HTMLImageElement).style.display = 'none'"
                />
                <span
                  v-if="imagesFor(l.type).length > 1"
                  class="thumb-count"
                  >{{ imagesFor(l.type).length }}</span
                >
              </button>
              <div class="n">{{ l.type }}</div>
              <div
                v-if="formatSizeRange(l.sizeSqftFrom, l.sizeSqftTo) || formatPrice(l.fromPriceAed)"
                class="meta"
              >
                <span v-if="formatSizeRange(l.sizeSqftFrom, l.sizeSqftTo)">{{
                  formatSizeRange(l.sizeSqftFrom, l.sizeSqftTo)
                }}</span>
                <span
                  v-if="formatPrice(l.fromPriceAed)"
                  class="price"
                  >{{ formatPrice(l.fromPriceAed) }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Fallback: bare unit-type names from unit_layouts (project has no unit_mix). -->
      <section
        v-else-if="unitTypes.length"
        class="block"
      >
        <div class="pp-desc">
          <div class="eyebrow">{{ t('property.v2Layouts.unitTypesEyebrow') }}</div>
          <h3>{{ t('property.v2Layouts.availableLayoutsTitle') }}</h3>
          <p>
            {{ t('property.v2Layouts.unitTypesDescription') }}
          </p>
        </div>
        <div class="types">
          <div
            v-for="(l, i) in unitTypes"
            :key="`${l.name}-${i}`"
            class="type"
            :class="{ 'has-thumb': (l.images?.length ?? 0) > 0 }"
          >
            <button
              v-if="(l.images?.length ?? 0) > 0"
              type="button"
              class="type-thumb"
              :aria-label="t('property.v2Layouts.viewLayoutAriaLabel')"
              @click="openLightbox(l.images ?? [], 0)"
            >
              <img
                :src="(l.images ?? [])[0]"
                alt=""
                loading="lazy"
                @error="($event.target as HTMLImageElement).style.display = 'none'"
              />
              <span
                v-if="(l.images?.length ?? 0) > 1"
                class="thumb-count"
                >{{ l.images?.length }}</span
              >
            </button>
            <div class="n">{{ l.name }}</div>
            <div
              v-if="hasMeta(l)"
              class="meta"
            >
              <span v-if="bedroomsLabel(l.bedrooms)">{{ bedroomsLabel(l.bedrooms) }}</span>
              <span v-if="formatArea(l.areaSqft)">{{ formatArea(l.areaSqft) }}</span>
              <span
                v-if="formatPrice(l.priceFromAed)"
                class="price"
                >{{ formatPrice(l.priceFromAed) }}</span
              >
            </div>
          </div>
        </div>
      </section>
    </template>

    <PropertyImageLightbox
      v-if="lightboxImages.length"
      :images="lightboxImages"
      :start-index="lightboxIndex"
      @close="lightboxImages = []"
    />
  </div>
</template>

<style scoped lang="scss">
.pp-tab-surface {
  background: #f8f7f3;
  border-radius: 20px;
  padding: 28px;
}

.empty {
  padding: 28px 0;
  color: #6e6f73;
  font-size: 14px;
}

.block + .block {
  margin-top: 28px;
}

.pp-desc {
  margin-bottom: 16px;

  .eyebrow {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #b8860b;
  }
  h3 {
    margin: 6px 0 4px;
    font-family: serif;
    font-size: 19px;
    font-weight: 600;
    color: #0d0f15;
  }
  p {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.5;
    color: #6e6f73;
    max-width: 60ch;
  }
}

.plan-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.plan {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
  }
}

.plan-wrap {
  position: relative;

  &.locked .plan-grid {
    filter: blur(11px);
    pointer-events: none;
    user-select: none;
  }
}

.lock-ov {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  color: #0d0f15;

  p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    max-width: 30ch;
  }
}

.lock-cta {
  padding: 10px 18px;
  border: 0;
  border-radius: 10px;
  background: #0d0f15;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #23262e;
  }
}

.docs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  @media (max-width: 744px) {
    grid-template-columns: 1fr;
  }
}

.doc {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 12px;
  color: #0d0f15;
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    border-color: rgba(13, 15, 21, 0.24);
  }
  svg {
    color: #b8860b;
    flex-shrink: 0;
  }
}

.cat-group + .cat-group {
  margin-top: 18px;
}

.cat-label {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(13, 15, 21, 0.55);
}

.types {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.type {
  padding: 12px 14px;
  background: #fff;
  border: 1px solid rgba(13, 15, 21, 0.08);
  border-radius: 12px;

  .n {
    font-weight: 600;
    font-size: 14px;
    color: #0d0f15;
  }
  .meta {
    margin-top: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 3px 9px;
    font-size: 12.5px;
    color: #6e6f73;

    .price {
      color: #0d0f15;
      font-weight: 600;
    }
  }
}

.type.has-thumb {
  padding: 0;
  overflow: hidden;

  .n {
    padding: 10px 14px 0;
  }
  .meta {
    padding: 0 14px 12px;
  }
}
.type-thumb {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border: none;
  padding: 0;
  background: #f3f3f4;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .thumb-count {
    position: absolute;
    top: 8px;
    inset-inline-end: 8px;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: rgba(13, 15, 21, 0.72);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
